import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'services', 'kurumsal', 'portfolyo' or null
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle hash scrolling after navigation
  useEffect(() => {
    if (isHomePage && location.hash) {
      const sectionId = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isHomePage, location.hash]);

  const handleNavigation = (sectionId) => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/?scrollTo=${sectionId}`);
    }
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const services = [
    { name: 'Tüm Hizmetler', path: '/hizmetler', icon: '📋' },
    { name: 'Mobil Uygulama', path: '/services/mobile-app-development', icon: '📱' },
    { name: 'Yapay Zeka Entegrasyonu', path: '/services/ai-integration', icon: '🤖' },
    { name: 'Web Tasarım & Geliştirme', path: '/services/web-design', icon: '🌐' },
    { name: 'Dijital Büyüme & SEO', path: '/services/digital-growth', icon: '📈' },
    { name: 'Sosyal Medya Yönetimi', path: '/services/social-media', icon: '📱' },
    { name: 'E-Ticaret Çözümleri', path: '/services/ecommerce', icon: '🛒' },
    { name: 'Medya Dağıtımı', path: '/services/media-distribution', icon: '📡' }
  ];

  const kurumsal = [
    { name: 'Hakkımızda', path: '/hakkimizda', icon: '🏢' },
    { name: 'Referanslarımız', path: '/referanslar', icon: '⭐' },
    { name: 'Kariyer', path: '/kariyer', icon: '💼' }
  ];

  const portfolyo = [
    { name: 'Tüm Çalışmalar', path: '/calismaalarimiz', icon: '🎨' },
    { name: 'Sosyal Medya', path: '/portfolyo/sosyal-medya', icon: '📱' },
    { name: 'SEO Projeleri', path: '/portfolyo/seo', icon: '🔍' },
    { name: 'Web Projeleri', path: '/portfolyo/web', icon: '💻' },
    { name: 'Mobil Uygulamalar', path: '/portfolyo/mobil-uygulama', icon: '📲' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/80 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <button 
            onClick={handleLogoClick}
            className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent hover:from-cyan-300 hover:to-teal-300 transition-all duration-300"
            data-testid="header-logo"
          >
            RUYAN
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* Services Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => toggleDropdown('services')}
                className="flex items-center gap-1 text-gray-300 hover:text-cyan-400 transition-colors duration-200 py-2"
                data-testid="header-services-btn"
              >
                Hizmetler
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'services' ? 'rotate-180' : ''}`} />
              </button>
              
              {openDropdown === 'services' && (
                <div className="absolute top-full left-0 mt-2 w-[520px] bg-slate-800/98 backdrop-blur-lg border border-cyan-500/30 rounded-xl shadow-2xl py-3 z-50">
                  <div className="grid grid-cols-2 gap-1">
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-cyan-400 hover:bg-slate-700/50 transition-colors rounded-lg mx-1"
                      >
                        <span className="text-xl">{service.icon}</span>
                        <span className="text-sm">{service.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Kurumsal Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => toggleDropdown('kurumsal')}
                className="flex items-center gap-1 text-gray-300 hover:text-cyan-400 transition-colors duration-200 py-2"
              >
                Kurumsal
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'kurumsal' ? 'rotate-180' : ''}`} />
              </button>
              
              {openDropdown === 'kurumsal' && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800/98 backdrop-blur-lg border border-cyan-500/30 rounded-xl shadow-2xl py-2 z-50">
                  {kurumsal.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-cyan-400 hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm">{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Portfolyo Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={() => toggleDropdown('portfolyo')}
                className="flex items-center gap-1 text-gray-300 hover:text-cyan-400 transition-colors duration-200 py-2"
              >
                Portfolyo
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'portfolyo' ? 'rotate-180' : ''}`} />
              </button>
              
              {openDropdown === 'portfolyo' && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800/98 backdrop-blur-lg border border-cyan-500/30 rounded-xl shadow-2xl py-2 z-50">
                  {portfolyo.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="flex items-center gap-3 px-4 py-2.5 text-gray-300 hover:text-cyan-400 hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm">{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/blog"
              className="text-gray-300 hover:text-cyan-400 transition-colors duration-200"
              data-testid="header-blog-link"
            >
              Blog
            </Link>
            <Link
              to="/sss"
              className="text-gray-300 hover:text-cyan-400 transition-colors duration-200"
            >
              SSS
            </Link>
            <Link
              to="/iletisim"
              className="text-gray-300 hover:text-cyan-400 transition-colors duration-200"
              data-testid="header-contact-link"
            >
              İletişim
            </Link>
          </nav>

          <Link to="/teklif-al">
            <Button
              className="hidden lg:block bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white transition-all duration-300 shadow-lg shadow-cyan-500/20"
              data-testid="header-cta-btn"
            >
              Teklif Al
            </Button>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-300 hover:text-cyan-400 transition-colors"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-slate-900">
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-4 border-b border-slate-800">
            <button 
              onClick={handleLogoClick}
              className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"
            >
              RUYAN
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-gray-300 hover:text-cyan-400 hover:bg-slate-700 transition-all"
            >
              <X size={24} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="h-[calc(100vh-73px)] overflow-y-auto">
            <nav className="px-4 py-6 space-y-6">
              {/* Hizmetler */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-6 bg-gradient-to-b from-cyan-500 to-teal-500 rounded-full"></div>
                  <h3 className="text-cyan-400 font-bold text-sm uppercase tracking-wider">Hizmetler</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-cyan-500/50 hover:bg-slate-800 transition-all duration-300"
                      style={{
                        animation: 'fade-in-up 0.3s ease-out both',
                        animationDelay: `${index * 0.05}s`
                      }}
                    >
                      <div className="text-2xl mb-1 group-hover:scale-110 transition-transform">{service.icon}</div>
                      <div className="text-gray-300 text-xs font-medium group-hover:text-cyan-400 transition-colors leading-tight">
                        {service.name}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Kurumsal */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-6 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                  <h3 className="text-purple-400 font-bold text-sm uppercase tracking-wider">Kurumsal</h3>
                </div>
                <div className="space-y-2">
                  {kurumsal.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-purple-500/50 hover:bg-slate-800 transition-all duration-300 group"
                      style={{
                        animation: 'fade-in-up 0.3s ease-out both',
                        animationDelay: `${(services.length + index) * 0.05}s`
                      }}
                    >
                      <div className="text-2xl group-hover:scale-110 transition-transform">{item.icon}</div>
                      <div className="text-gray-300 text-sm font-medium group-hover:text-purple-400 transition-colors">
                        {item.name}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Portfolyo */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-amber-500 rounded-full"></div>
                  <h3 className="text-orange-400 font-bold text-sm uppercase tracking-wider">Portfolyo</h3>
                </div>
                <div className="space-y-2">
                  {portfolyo.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-orange-500/50 hover:bg-slate-800 transition-all duration-300 group"
                      style={{
                        animation: 'fade-in-up 0.3s ease-out both',
                        animationDelay: `${(services.length + kurumsal.length + index) * 0.05}s`
                      }}
                    >
                      <div className="text-2xl group-hover:scale-110 transition-transform">{item.icon}</div>
                      <div className="text-gray-300 text-sm font-medium group-hover:text-orange-400 transition-colors">
                        {item.name}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Diğer Sayfalar */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-green-500 rounded-full"></div>
                  <h3 className="text-emerald-400 font-bold text-sm uppercase tracking-wider">Diğer</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/blog"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-emerald-500/50 hover:bg-slate-800 transition-all group"
                  >
                    <span className="text-xl group-hover:scale-110 transition-transform">📝</span>
                    <span className="text-gray-300 text-xs font-medium group-hover:text-emerald-400 transition-colors">Blog</span>
                  </Link>
                  <Link
                    to="/sss"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-emerald-500/50 hover:bg-slate-800 transition-all group"
                  >
                    <span className="text-xl group-hover:scale-110 transition-transform">❓</span>
                    <span className="text-gray-300 text-xs font-medium group-hover:text-emerald-400 transition-colors">SSS</span>
                  </Link>
                  <Link
                    to="/iletisim"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 hover:border-emerald-500/50 hover:bg-slate-800 transition-all group"
                  >
                    <span className="text-xl group-hover:scale-110 transition-transform">📧</span>
                    <span className="text-gray-300 text-xs font-medium group-hover:text-emerald-400 transition-colors">İletişim</span>
                  </Link>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 pt-4">
                <Link to="/teklif-al" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-base font-semibold rounded-xl shadow-lg shadow-cyan-500/30 hover:scale-105 transition-all">
                    🚀 Teklif Al
                  </Button>
                </Link>
                <Link to="/web-analiz" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-slate-800 hover:bg-slate-700 text-white py-6 text-base font-semibold rounded-xl border border-slate-700 hover:border-cyan-500/50 transition-all">
                    🔍 Ücretsiz Web Analiz
                  </Button>
                </Link>
              </div>

              {/* Bottom Spacing */}
              <div className="h-8"></div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
